import React, { useState } from 'react';
import { Message } from '@/type/chat';
import { Pencil, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { useSnackbar } from '@/contexts/SnackbarContext';

export interface EditMessageProps {
  message: Message;
  chatId: string;
  onEdit: (messageId: string, newContent: string, createBranch: boolean) => void;
}

export default function EditMessage({ message, chatId, onEdit }: EditMessageProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(message.content);
  const [createBranch, setCreateBranch] = useState(true);
  const { showError, showInfo } = useSnackbar();

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditedContent(message.content);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedContent(message.content);
  };

  const handleSave = () => {
    if (!editedContent.trim()) {
      showError(t('Message content cannot be empty'));
      return;
    }

    if (editedContent === message.content) {
      showInfo(t('Content has not changed'));
      setIsEditing(false);
      return;
    }

    onEdit(message.messageId, editedContent, createBranch);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl + Enter to save
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
    // Esc to cancel
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="w-full space-y-3">
        {/* Edit area */}
        <div className="relative">
          <textarea
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            onKeyDown={handleKeyDown}
            className={cn(
              'w-full min-h-[100px] p-3 rounded-lg border',
              'bg-bg-main',
              'border-gray-300 dark:border-gray-600',
              'text-text-primary',
              'focus:outline-hidden focus:ring-2 focus:ring-blue-500',
              'resize-y',
            )}
            placeholder={t('Edit your question...')}
            autoFocus
          />
          <div className="absolute bottom-2 right-2 text-xs text-text-secondary">
            {t('Cmd+Enter to save')} · {t('Esc to cancel')}
          </div>
        </div>

        {/* Options and buttons */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={createBranch}
              onChange={(e) => setCreateBranch(e.target.checked)}
              className="w-4 h-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-text-primary">
              {t('Keep original question and answer (create new branch)')}
            </span>
          </label>

          <div className="flex items-center gap-2">
            <button
              onClick={handleCancel}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg',
                'text-sm font-medium',
                'text-text-primary',
                ' bg-bg-main',
                'hover:bg-gray-200 dark:hover:bg-gray-600',
                'transition-colors',
              )}
            >
              <X size={16} />
              {t('Cancel')}
            </button>
            <button
              onClick={handleSave}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg',
                'text-sm font-medium',
                'text-white',
                'bg-blue-600 hover:bg-blue-700',
                'transition-colors',
              )}
            >
              <Check size={16} />
              {t('Save')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={handleStartEdit}
      className={cn(
        'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg',
        'text-xs font-medium',
        'text-text-secondary',
        'hover:text-black dark:hover:text-white',
        'hover:bg-gray-100 dark:hover:bg-gray-700',
        'transition-colors',
      )}
      title={t('Edit this message')}
    >
      <Pencil size={14} />
      {t('Edit')}
    </button>
  );
}
